import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiRadioItem from '../../ui/base-components/pai-radio-item';
import PaiButton from '../../ui/base-components/pai-button';
import PaiTextField from '../../ui/base-components/pai-text-field';
import {
  EmailAccountTypes,
  EmailCredentialErrors,
  EmailCredentials,
  Encryptions,
  imapEncryptionOptions,
  OAuthCredentials,
  smtpEncryptionOptions,
} from './linkedin-accounts.types';

interface Props {
  open: boolean;
  type: EmailAccountTypes;
  smtp: EmailCredentials;
  imap: EmailCredentials;
  oauth: OAuthCredentials;
  error: EmailCredentialErrors | undefined;
  accessRevoked: boolean;
  loading: boolean;

  handleEmailAccountTypeChange(value: string): () => void;

  handleSmtpEmailCredentialsChange(field: keyof EmailCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSmtpEncryptionChange(value: string): () => void;

  handleImapEmailCredentialsChange(field: keyof EmailCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleImapEncryptionChange(value: string): () => void;

  handleOAuthEmailCredentialsChange(field: keyof OAuthCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleClose(): void;

  handleAcceptEmailCredentials(): void;
}

interface OtherProps {
  smtp: EmailCredentials;
  imap: EmailCredentials;
  error: EmailCredentialErrors | undefined;

  handleSmtpEmailCredentialsChange(field: keyof EmailCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSmtpEncryptionChange(value: string): () => void;

  handleImapEmailCredentialsChange(field: keyof EmailCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleImapEncryptionChange(value: string): () => void;
}

interface OauthProps {
  oauth: OAuthCredentials;
  accessRevoked: boolean;

  handleOAuthEmailCredentialsChange(field: keyof OAuthCredentials): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const OtherMask: React.FC<OtherProps> = ({
  smtp,
  imap,
  error,
  handleSmtpEmailCredentialsChange,
  handleSmtpEncryptionChange,
  handleImapEmailCredentialsChange,
  handleImapEncryptionChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <PaiTextField
        value={smtp.server}
        label={t('serverSmtp')}
        placeholder={`${t('forExampleShort')} smtp.example.com`}
        error={error === EmailCredentialErrors.SMTP}
        errorText={t('invalidSmtpServer')}
        onChange={handleSmtpEmailCredentialsChange('server')}
      />
      <PaiTextField
        value={smtp.port}
        label={t('portSmtp')}
        placeholder={`${t('forExampleShort')} 465 / 587 / 2525`}
        error={error === EmailCredentialErrors.SMTP}
        errorText={t('invalidSmtpServer')}
        onChange={handleSmtpEmailCredentialsChange('port')}
      />
      <Stack gap={1}>
        <Typography className={classes.label}>{t('security')}</Typography>
        <Stack direction={'row'} gap={2} padding={'0 4px 10px 4px'}>
          {smtpEncryptionOptions.map((option) => (
            <PaiRadioItem
              key={option.value}
              label={option.value === Encryptions.NONE ? t(_.lowerCase(option.label)) : option.label}
              value={option.value}
              selectedValue={smtp.encryption}
              variant="row"
              handleSelectItem={handleSmtpEncryptionChange}
            />
          ))}
        </Stack>
      </Stack>
      <Divider />
      <PaiTextField
        value={imap.server}
        label={t('serverImap')}
        placeholder={`${t('forExampleShort')} outlook.example.com`}
        error={error === EmailCredentialErrors.SMTP}
        errorText={t('invalidImapServer')}
        onChange={handleImapEmailCredentialsChange('server')}
      />
      <PaiTextField
        value={imap.port}
        label={t('portImap')}
        placeholder={`${t('forExampleShort')} 143 / 993`}
        error={error === EmailCredentialErrors.SMTP}
        errorText={t('invalidImapServer')}
        onChange={handleImapEmailCredentialsChange('port')}
      />
      <Stack gap={1}>
        <Typography className={classes.label}>{t('security')}</Typography>
        <Stack direction={'row'} gap={2} padding={'0 4px 10px 4px'}>
          {imapEncryptionOptions.map((option) => (
            <PaiRadioItem
              key={option.value}
              label={option.value === Encryptions.NONE ? t(_.lowerCase(option.label)) : option.label}
              value={option.value}
              selectedValue={imap.encryption}
              variant="row"
              handleSelectItem={handleImapEncryptionChange}
            />
          ))}
        </Stack>
      </Stack>
      <Divider />
      <PaiTextField
        value={smtp.username}
        label={`${t('username')} (${t('emailShort')})`}
        placeholder={`${t('forExampleShort')} user@example.com`}
        error={error === EmailCredentialErrors.CREDENTIALS}
        errorText={t('invalidCredentials')}
        onChange={handleSmtpEmailCredentialsChange('username')}
      />
      <PaiTextField
        value={smtp.password}
        label={t('password')}
        type={'password'}
        error={error === EmailCredentialErrors.CREDENTIALS}
        errorText={t('invalidCredentials')}
        onChange={handleSmtpEmailCredentialsChange('password')}
      />
    </>
  );
};

const MsOfficeMask: React.FC<OauthProps> = ({ oauth, accessRevoked, handleOAuthEmailCredentialsChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const authMsAccountURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=22437d1b-8d8e-40f8-a71f-d688a9615269&response_type=code&redirect_uri=${window.location.origin}/dashboard${Routes.OAUTH_MICROSOFT_SUCCESS}&scope=user.read offline_access Mail.Send Mail.read openid&response_mode=query&prompt=consent&hint=${oauth.email}`;

  const openMsAuth = () => {
    window.open(authMsAccountURL, '_blank');
  };

  return (
    <>
      <PaiTextField
        value={oauth.email}
        label={`${t('username')} (${t('emailShort')})`}
        placeholder={`${t('forExampleShort')} user@example.com`}
        onChange={handleOAuthEmailCredentialsChange('email')}
      />
      <Stack gap={1}>
        <Typography className={classes.longInstruction}>{t('authMsAccountInstruction')}</Typography>
        {accessRevoked ? <Typography className={cx(classes.longInstruction, 'warning')}>{t('emailAccessRevoked')}</Typography> : null}
        <Stack direction={'row'} justifyContent={'center'}>
          <PaiButton text={t('authMsAccount')} border={'rounded'} disabled={oauth.email.trim() === ''} action={openMsAuth} />
        </Stack>
      </Stack>
      <PaiTextField value={oauth.code} label={t('authCode')} placeholder={t('enterCodeHere')} onChange={handleOAuthEmailCredentialsChange('code')} />
    </>
  );
};

const GmailMask: React.FC<OauthProps> = ({ oauth, accessRevoked, handleOAuthEmailCredentialsChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const authGmailAccountURL = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${window.location.origin}/dashboard${Routes.OAUTH_GMAIL_SUCCESS}&prompt=consent&response_type=code&client_id=184485567772-d9itn31afp3dl1k75nsqms5uf6feldcp.apps.googleusercontent.com&scope=https%3A%2F%2Fmail.google.com%2F&access_type=offline`;

  const openGmailAuth = () => {
    window.open(authGmailAccountURL, '_blank');
  };

  return (
    <>
      <PaiTextField
        value={oauth.email}
        label={`${t('username')} (${t('emailShort')})`}
        placeholder={`${t('forExampleShort')} user@example.com`}
        onChange={handleOAuthEmailCredentialsChange('email')}
      />
      <Stack gap={1}>
        <Typography className={classes.longInstruction}>{t('authGmailAccountInstruction')}</Typography>
        {accessRevoked ? <Typography className={cx(classes.longInstruction, 'warning')}>{t('emailAccessRevoked')}</Typography> : null}
        <Stack direction={'row'} justifyContent={'center'}>
          <PaiButton text={t('authGmailAccount')} border={'rounded'} disabled={oauth.email.trim() === ''} action={openGmailAuth} />
        </Stack>
      </Stack>
      <PaiTextField value={oauth.code} label={t('authCode')} placeholder={t('enterCodeHere')} onChange={handleOAuthEmailCredentialsChange('code')} />
    </>
  );
};

const EditEmailCredentialsDialog: React.FC<Props> = ({
  open,
  type,
  smtp,
  imap,
  oauth,
  error,
  accessRevoked,
  loading,
  handleEmailAccountTypeChange,
  handleSmtpEmailCredentialsChange,
  handleSmtpEncryptionChange,
  handleImapEmailCredentialsChange,
  handleImapEncryptionChange,
  handleOAuthEmailCredentialsChange,
  handleClose,
  handleAcceptEmailCredentials,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { server: smtpServer, port: smtpPort, username, password } = smtp;
  const smtpDisabled = !smtpServer || !smtpPort || !username || !password;

  const { server: imapServer, port: imapPort } = imap;
  const imapDisabled = !imapServer || !imapPort || !username || !password;

  const otherDisabled = smtpDisabled || imapDisabled;

  const { email, code } = oauth;
  const oauthDisabled = !email || !code;

  const disabled = type === EmailAccountTypes.MS_O365 || type === EmailAccountTypes.GMAIL ? oauthDisabled : otherDisabled;

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('emailAccountCredentials')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Stack gap={1}>
            <Typography className={classes.label}>{t('accountType')}</Typography>
            <Stack direction={'row'} gap={2} padding={'0 4px 10px 4px'}>
              {_.map(EmailAccountTypes, (option) => (
                <PaiRadioItem
                  key={option}
                  label={t(option)}
                  value={option}
                  selectedValue={type}
                  variant="row"
                  handleSelectItem={handleEmailAccountTypeChange}
                />
              ))}
            </Stack>
          </Stack>
          <Divider />
          {type === EmailAccountTypes.OTHER ? (
            <OtherMask
              smtp={smtp}
              imap={imap}
              error={error}
              handleSmtpEmailCredentialsChange={handleSmtpEmailCredentialsChange}
              handleSmtpEncryptionChange={handleSmtpEncryptionChange}
              handleImapEmailCredentialsChange={handleImapEmailCredentialsChange}
              handleImapEncryptionChange={handleImapEncryptionChange}
            />
          ) : type === EmailAccountTypes.MS_O365 ? (
            <MsOfficeMask oauth={oauth} accessRevoked={accessRevoked} handleOAuthEmailCredentialsChange={handleOAuthEmailCredentialsChange} />
          ) : type === EmailAccountTypes.GMAIL ? (
            <GmailMask oauth={oauth} accessRevoked={accessRevoked} handleOAuthEmailCredentialsChange={handleOAuthEmailCredentialsChange} />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleAcceptEmailCredentials} disabled={disabled} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '20px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  label: {
    marginBottom: '4px',
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
  },
  longInstruction: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    marginBottom: '8px',
    '&.highlight': {
      color: theme.palette.error.main,
      fontWeight: 'bold',
    },
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditEmailCredentialsDialog;
